import { useEffect, useRef, useState } from "react";
import { MessageCircle, X } from "lucide-react";

import { TranslatedText } from "@/components/common/text";
import { ClickOutside } from "@/components/common/other/ClickOutside";
import { API } from "@/services/API";
import { cn } from "@/utils/css";

import { ChatAssistantHeader } from "./ChatAssistantHeader";
import { ChatAssistantEmpty } from "./ChatAssistantEmpty";
import { ChatMessages } from "./ChatMessages";
import { ChatInput } from "./ChatInput";

export const ChatAssistant = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [text, setText] = useState("");
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(false);
    const inputRef = useRef(null);
    const launcherRef = useRef(null);

    useEffect(() => {
        if (isOpen) {
            setTimeout(() => inputRef.current?.focus(), 500);
        }
    }, [isOpen]);

    const handleSend = () => {
        const message = text.trim();
        if (!message || loading) return;

        setMessages((prev) => [...prev, { role: "user", text: message }]);
        setText("");
        setLoading(true);

        API.post("/assistant/chat", { message })
            .then((response) => {
                setMessages((prev) => [...prev, { role: "assistant", text: response.reply }]);
            })
            .catch((error) => {
                const errorMessage = typeof error === "string" ? error : "Something went wrong. Please try again.";
                setMessages((prev) => [...prev, { role: "assistant", text: errorMessage }]);
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const handleToggle = () => {
        setIsOpen((prev) => !prev);
    };

    const handleClickAway = (event) => {
        if (launcherRef.current && !launcherRef.current.contains(event.target)) {
            setIsOpen(false);
        }
    };

    return (
        <div className="fixed bottom-5 right-5 flex flex-col items-end z-floating">
            <ClickOutside onClick={handleClickAway}>
                <div
                    className={cn(
                        "box-border w-[477px] bg-cm-p-background rounded-[12px] overflow-hidden border border-cm-p-outline shadow-[0px_0px_14px_-3px_rgba(93,93,93,0.16)] transition-all duration-500 ease-in-out",
                        isOpen ? "h-[527px]" : "h-0 border-0 shadow-none",
                    )}
                >
                    <div
                        className={cn(
                            "flex flex-col w-full h-full transition-[visibility] duration-500 ease-in-out",
                            isOpen ? "visible" : "invisible",
                        )}
                    >
                        <ChatAssistantHeader onClose={() => setIsOpen(false)} />
                        {messages.length === 0 && !loading ? (
                            <ChatAssistantEmpty />
                        ) : (
                            <ChatMessages messages={messages} loading={loading} />
                        )}
                        <ChatInput
                            value={text}
                            onChange={setText}
                            onSubmit={handleSend}
                            disabled={!text.trim() || loading}
                            inputRef={inputRef}
                        />
                    </div>
                </div>
            </ClickOutside>
            <div className="mt-4">
                <button
                    type="button"
                    ref={launcherRef}
                    onClick={handleToggle}
                    className={cn(
                        "min-w-[52px] h-[52px] bg-cm-p-surface-container rounded-lg inline-flex items-center justify-center text-cm-p-text-secondary shadow-[inset_0_0_0_1px_rgba(0,0,0,0.03)]",
                        { "bg-cm-p-accent-100 pl-5 pr-6": !isOpen },
                    )}
                >
                    {isOpen ? (
                        <X size={24} />
                    ) : (
                        <span className="text-cm-p-text-primary font-medium inline-flex gap-2 items-center">
                            <MessageCircle className="w-4 h-4" />
                            <TranslatedText className="text-sm">CM Assistant</TranslatedText>
                        </span>
                    )}
                </button>
            </div>
        </div>
    );
};
